import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  Output,
  Type,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { AttributeComponentRegistry } from '../registry/attribute-component-registry.service';

export type FieldRendererMode = 'display' | 'input' | 'filter';

export interface FieldRendererField {
  key: string;
  type: string;
  value?: unknown;
  options?: { label: string; value: string }[];
  showClear?: boolean;
  emptyMessage?: string;
  /** For record-type fields: slug of the target definition to load instances from. */
  targetDefinitionSlug?: string;
  /** For record-type fields: human-readable title of the target definition. */
  targetDefinitionTitle?: string;
  /** For record-type fields: display label for the currently selected chip. */
  chipLabel?: string;
  /** For record-type fields: whether the selected record was just created (shows "· new"). */
  isNewlyCreated?: boolean;
  /**
   * For relation-type fields: cardinality of targets reachable from this side. When 'MANY'
   * the input renders as multi-select and the value is `string[]`; when 'ONE' it's a
   * single-select with a `string` value. Defaults to 'ONE' if unspecified.
   */
  cardinality?: 'ONE' | 'MANY';
}

/**
 * Unified field renderer that delegates to the registered display or input component
 * for the given field type.
 *
 * Usage:
 * ```html
 * <!-- display mode -->
 * <tf-field-renderer [fieldDef]="field" mode="display" />
 *
 * <!-- input/filter mode -->
 * <tf-field-renderer [fieldDef]="field" mode="input" [value]="val" (valueChange)="onValueChange($event)" />
 * ```
 *
 * Note: the input is named `fieldDef` (not `field`) to avoid colliding with the
 * `[field]` directive selector from `@angular/forms/signals`.
 *
 * Plugins can override any type by registering in AttributeComponentRegistry.
 */
@Component({
  selector: 'tf-field-renderer',
  standalone: true,
  template: `<ng-container #anchor />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldRendererComponent implements OnDestroy {
  private readonly anchor = viewChild.required('anchor', { read: ViewContainerRef });

  private readonly registry = inject(AttributeComponentRegistry);

  readonly fieldDef = input.required<FieldRendererField>();
  readonly mode = input<FieldRendererMode>('display');
  readonly value = input<unknown>(undefined);
  readonly invalid = input<boolean>(false);
  @Output() readonly valueChange = new EventEmitter<unknown>();
  /**
   * Emitted by record/relation-type inputs when the user clicks "+ Create new". The payload is
   * an optional query string — the typed text from autocomplete pickers (e.g. the Tags picker's
   * "Create '<query>'" suggestion). The generic relation input emits without a payload, so the
   * union allows `undefined`.
   */
  @Output() readonly createNewRequested = new EventEmitter<string | undefined>();
  /** Emitted by record-type inputs when the user removes the selected chip. */
  @Output() readonly removeRequested = new EventEmitter<void>();

  private compRef: ComponentRef<Record<string, unknown>> | null = null;
  private outSubs: { unsubscribe(): void }[] = [];

  /**
   * Resolved component type — stable reference that only changes when the
   * TYPE or MODE actually changes, not on every new fieldDef object reference.
   */
  private readonly activeType = computed<Type<unknown>>(() => {
    const components = this.registry.resolve(this.fieldDef());
    return this.mode() === 'display' ? components.display : components.input;
  });

  constructor() {
    // Re-mount child component only when the resolved Type reference changes.
    effect(() => {
      const type = this.activeType(); // tracked — stable class reference
      untracked(() => this.mountComponent(type as Type<Record<string, unknown>>));
    });

    // Forward value + options + invalid to the mounted component on every relevant change.
    effect(() => {
      const val = this.value();      // tracked
      const f = this.fieldDef();     // tracked (options or display value may change)
      const inv = this.invalid();    // tracked
      untracked(() => this.syncInputs(val, f, inv));
    });
  }

  ngOnDestroy(): void {
    this.outSubs.forEach((s) => s.unsubscribe());
    this.compRef?.destroy();
  }

  private mountComponent(type: Type<Record<string, unknown>>): void {
    this.outSubs.forEach((s) => s.unsubscribe());
    this.outSubs = [];
    this.compRef?.destroy();
    this.anchor().clear();

    this.compRef = this.anchor().createComponent(type);

    const valueEmitter = this.compRef.instance['valueChange'];
    if (valueEmitter instanceof EventEmitter) {
      this.outSubs.push(valueEmitter.subscribe((v: unknown) => this.valueChange.emit(v)));
    }
    const createEmitter = this.compRef.instance['createNewRequested'];
    if (createEmitter instanceof EventEmitter) {
      this.outSubs.push(createEmitter.subscribe((query: string | undefined) => this.createNewRequested.emit(query)));
    }
    const removeEmitter = this.compRef.instance['removeRequested'];
    if (removeEmitter instanceof EventEmitter) {
      this.outSubs.push(removeEmitter.subscribe(() => this.removeRequested.emit()));
    }

    // Apply current inputs right after creation.
    this.syncInputs(
      untracked(() => this.value()),
      untracked(() => this.fieldDef()),
      untracked(() => this.invalid()),
    );
  }

  private syncInputs(val: unknown, f: FieldRendererField, inv: boolean): void {
    if (!this.compRef) return;
    if (untracked(() => this.mode()) === 'display') {
      this.compRef.setInput('value', f.value);
    } else {
      this.compRef.setInput('value', val);
      this.compRef.setInput('invalid', inv);
      this.forwardOptionalInputs(f);
    }
  }

  private forwardOptionalInputs(f: FieldRendererField): void {
    const inst = this.compRef?.instance;
    if (!inst) return;
    const optionals: [string, unknown][] = [
      ['options', f.options ?? []],
      ['showClear', f.showClear ?? false],
      ['emptyMessage', f.emptyMessage ?? ''],
      ['targetDefinitionSlug', f.targetDefinitionSlug ?? ''],
      ['targetDefinitionTitle', f.targetDefinitionTitle ?? ''],
      ['chipLabel', f.chipLabel ?? ''],
      ['isNewlyCreated', f.isNewlyCreated ?? false],
      ['cardinality', f.cardinality ?? 'ONE'],
    ];
    for (const [key, value] of optionals) {
      if (key in inst) this.compRef?.setInput(key, value);
    }
  }
}
